// Business Administration Service Layer
// Feature: 005-business-administration
// Updated: 007-cloudflare-migration - migrated to Cloudflare API Gateway

import * as Sentry from '@sentry/react';
import { listBusinesses, getBusiness, updateBusiness } from './api/admin';
import { get } from './api/client';
import { uploadImages, deleteImages, extractImageKey } from './api/images';
import { isApiError } from './api/errors';
import type { ApiBusiness, ApiBusinessListItem, BusinessListParams, PaginatedResponse } from './api/types';
import type {
  BusinessSummary,
  BusinessWithOwner,
  BusinessStatusChange,
  BusinessProfileUpdate,
  BusinessPhotoOperation,
  BusinessListResponse,
  CategoryOption,
  BusinessStatus,
  PaginationMetadata,
} from '../types/businessAdmin';

/**
 * Transform API business list item to legacy BusinessSummary format
 * Note: category contains UUID, will be enriched with name in hook
 */
function transformBusinessListItem(apiBusiness: ApiBusinessListItem): BusinessSummary {
  return {
    id: apiBusiness.id,
    name: apiBusiness.name,
    status: apiBusiness.status,
    category: apiBusiness.category || 'Uncategorized', // UUID, enriched later with name
    photoUrl: apiBusiness.photos?.[0] ?? '',
    ownerEmail: undefined, // Not available in list view
    createdAt: apiBusiness.created_at,
    updatedAt: apiBusiness.created_at, // Use created_at as fallback
  };
}

/**
 * Transform API business to legacy BusinessWithOwner format
 */
function transformBusiness(apiBusiness: ApiBusiness): BusinessWithOwner {
  return {
    id: apiBusiness.id,
    data: {
      name: apiBusiness.name,
      description: apiBusiness.description ?? '',
      category: apiBusiness.category,
      categoryName: apiBusiness.category_name || 'Uncategorized',
      status: apiBusiness.status,
      city: apiBusiness.city,
      location: apiBusiness.location,
      photo_url: apiBusiness.photos?.[0] || '',
      photos: apiBusiness.photos || [],
      tags: apiBusiness.tags || [],
    },
    created_by: apiBusiness.user_id,
    created_at: apiBusiness.created_at,
    updated_at: apiBusiness.updated_at,
    ownerEmail: undefined,
    ownerName: undefined,
  };
}

/**
 * Get all businesses with search, filter, and pagination support
 */
export async function getAllBusinesses(
  searchTerm: string = '',
  statusFilter: BusinessStatus | 'all' = 'all',
  page: number = 1,
  pageSize: number = 50
): Promise<BusinessListResponse> {
  try {
    const params: BusinessListParams = {
      limit: pageSize,
    };

    if (searchTerm) {
      params.q = searchTerm;
    }

    if (statusFilter !== 'all') {
      params.status = statusFilter as any;
    }

    const response: PaginatedResponse<ApiBusinessListItem> = await listBusinesses(params);

    const businesses = response.data.map(transformBusinessListItem);
    const totalCount = response.pagination.total;
    const totalPages = Math.ceil(totalCount / pageSize);

    const pagination: PaginationMetadata = {
      currentPage: page,
      totalPages,
      pageSize,
      totalCount,
      hasMore: response.pagination.hasMore,
    };

    return { businesses, pagination };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'business-admin', action: 'getAllBusinesses' },
        contexts: {
          query: { searchTerm, statusFilter, page, pageSize },
        },
      });
    }
    throw error;
  }
}

/**
 * Get complete details for a specific business
 */
export async function getBusinessDetails(businessId: string): Promise<BusinessWithOwner | null> {
  try {
    const apiBusiness = await getBusiness(businessId);
    return transformBusiness(apiBusiness);
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'business-admin', action: 'getBusinessDetails' },
        extra: { businessId },
      });
    }
    return null;
  }
}

/**
 * Change business status (approve/disable/reactivate)
 */
export async function changeBusinessStatus(
  change: BusinessStatusChange
): Promise<{ success: boolean; error?: string }> {
  try {
    const { businessId, newStatus, reason } = change;

    // Validate reason is provided when disabling
    if (newStatus === 'disabled' && !reason) {
      return { success: false, error: 'Reason is required when disabling a business' };
    }

    await updateBusiness(businessId, { status: newStatus as any });

    // Log status change for audit trail
    Sentry.addBreadcrumb({
      category: 'business-admin',
      message: `Business status changed to ${newStatus}`,
      level: 'info',
      data: { businessId, newStatus, reason: reason || 'none' },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'business-admin', action: 'changeBusinessStatus' },
        extra: {
          businessId: change.businessId,
          newStatus: change.newStatus,
          reason: change.reason,
        },
      });
    }
    return { success: false, error: 'Failed to change business status' };
  }
}

/**
 * Update business profile information
 */
export async function updateBusinessProfile(
  businessId: string,
  updates: BusinessProfileUpdate
): Promise<{ success: boolean; errors?: Record<string, string> }> {
  try {
    // Build update payload with all provided fields
    const updatePayload: Record<string, unknown> = {};
    if (updates.name !== undefined) updatePayload.name = updates.name;
    if (updates.description !== undefined) updatePayload.description = updates.description;
    if (updates.category_id !== undefined) updatePayload.category_id = updates.category_id;
    if (updates.city !== undefined) updatePayload.city = updates.city;
    if (updates.location !== undefined) updatePayload.location = updates.location;
    if (updates.tags !== undefined) updatePayload.tags = updates.tags;

    await updateBusiness(businessId, updatePayload);

    // Log profile update for audit trail
    Sentry.addBreadcrumb({
      category: 'business-admin',
      message: 'Business profile updated',
      level: 'info',
      data: { businessId, updatedFields: Object.keys(updates) },
    });

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'business-admin', action: 'updateBusinessProfile' },
        extra: { businessId, updatedFields: Object.keys(updates) },
      });
    }
    return { success: false, errors: { general: 'Failed to update business profile' } };
  }
}

/**
 * Update business photos using R2 Image Handler API
 */
export async function updateBusinessPhotos(
  businessId: string,
  operations: BusinessPhotoOperation
): Promise<{ success: boolean; error?: string }> {
  try {
    const uploadedUrls: string[] = [];
    
    // Handle photo additions - upload to R2
    if (operations.add && operations.add.length > 0) {
      const uploadResult = await uploadImages(operations.add, 'business');
      
      if (!uploadResult.success || uploadResult.errors.length > 0) {
        const errorMsg = uploadResult.errors[0]?.error || 'Failed to upload photos';
        return { success: false, error: errorMsg };
      }
      
      // Collect the public URLs from uploaded images
      uploadedUrls.push(...uploadResult.images.map(img => img.publicUrl));
    }

    // Handle photo removals - delete from R2
    if (operations.remove && operations.remove.length > 0) {
      // Extract keys from URLs
      const keysToDelete = operations.remove
        .map(url => extractImageKey(url))
        .filter((key): key is string => key !== null);
      
      if (keysToDelete.length > 0) {
        const deleteResult = await deleteImages(keysToDelete);
        
        if (!deleteResult.success) {
          Sentry.addBreadcrumb({
            category: 'business-admin',
            message: `Some photos failed to delete: ${deleteResult.failed.length} failed`,
            level: 'warning',
            data: { failed: deleteResult.failed },
          });
          // Continue anyway - photos might already be deleted
        }
      }
    }

    // If we uploaded new photos or have primary change, update the business
    // TODO: When API supports photo array update, send the new photo list
    if (uploadedUrls.length > 0 || operations.setPrimary) {
      Sentry.addBreadcrumb({
        category: 'business-admin',
        message: 'Business photos updated',
        level: 'info',
        data: {
          businessId,
          photosAdded: uploadedUrls.length,
          photosRemoved: operations.remove?.length || 0,
          primaryChanged: !!operations.setPrimary,
        },
      });
    }

    return { success: true };
  } catch (error) {
    if (!isApiError(error)) {
      Sentry.captureException(error, {
        tags: { feature: 'business-admin', action: 'updateBusinessPhotos' },
        extra: { businessId },
      });
    }
    return { success: false, error: 'Failed to update business photos' };
  }
}

/**
 * Get available categories for dropdown
 * Fetches from /v1/categories API endpoint filtered by business feature
 */
export async function getCategories(): Promise<CategoryOption[]> {
  try {
    const response = await get<{ categories: Array<{ id: string; name: string }> }>('/categories?feature=business&lang=en');
    
    return response.categories.map(cat => ({
      id: cat.id,
      name: cat.name || 'Unknown',
    }));
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'business-admin', action: 'getCategories' },
    });
    return [];
  }
}
